import React, { useEffect, useState } from "react";
import { tourStepsShop } from "./tourStepsShop";
import GuidedTour from "../../components/GuidedTour";
import { useTheme } from "../../context/ThemeContext";
import { useAuth0 } from "@auth0/auth0-react";
import BubbleText from "../../components/BubbleText";
import { useCart } from "../../context/CartContext";
import Cart from "../../components/Cart";
import { useSearchParams } from "react-router-dom";

const LICENSES = {
  single: {
    name: "Single License",
    price: 29.95,
  },
  multi: {
    name: "Multi License Pack",
    price: 69.95,
  },
} as const;

type LicenseKey = keyof typeof LICENSES;

/**
 * @fileoverview E-commerce component for premium license purchases.
 *
 * Quantity is adjusted in the cart modal. Checkout uses Stripe test mode only.
 */
const Shop: React.FC = () => {
  const [isTourRunning, setIsTourRunning] = useState<boolean>(false);
  const [currentTourStep, setCurrentTourStep] = useState<number>(0);
  const { theme } = useTheme();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const { items, addItem, removeItem, updateQuantity, clearCart } = useCart();
  const [isCartOpen, setIsCartOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [checkoutNotice, setCheckoutNotice] = useState<string | null>(null);

  useEffect(() => {
    const tourCompleted = localStorage.getItem("tourCompleted");
    if (!tourCompleted) {
      setIsTourRunning(true);
    }
  }, []);

  useEffect(() => {
    const checkout = searchParams.get("checkout");
    if (checkout === "success") {
      clearCart();
      setCheckoutNotice(
        "Test checkout completed. No real money was charged in Stripe test mode.",
      );
      setSearchParams({}, { replace: true });
    } else if (checkout === "cancel") {
      setCheckoutNotice("Checkout was canceled. Your cart is still available.");
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, clearCart]);

  const handleTourComplete = () => {
    setIsTourRunning(false);
    localStorage.setItem("tourCompleted", "true");
  };

  const handleTourStepChange = (step: number) => {
    setCurrentTourStep(step);
  };

  const handleAddToCart = (key: LicenseKey) => {
    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: "/shop" },
      });
      return;
    }

    const license = LICENSES[key];
    addItem({
      name: license.name,
      price: license.price,
      quantity: 1,
    });
    setIsCartOpen(true);
  };

  const cartCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="page-container">
      <Cart
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        items={items}
        onRemoveItem={removeItem}
        onUpdateQuantity={updateQuantity}
      />
      <div
        className="flex w-full flex-col items-center text-theme-text"
        style={{
          backgroundColor: theme.backgroundColor,
          color: theme.textColor,
        }}
      >
        <h1 className="mb-8 text-3xl font-bold">
          <BubbleText>Race The Clock Premium</BubbleText>
        </h1>

        {checkoutNotice && (
          <div
            className="mb-6 max-w-4xl rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 text-center text-sm text-emerald-900"
            role="status"
          >
            {checkoutNotice}
            <button
              type="button"
              className="ml-3 underline"
              onClick={() => setCheckoutNotice(null)}
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="mb-6 flex flex-wrap items-center justify-center gap-3">
          <p className="text-sm opacity-80">
            Add a license to your cart, then adjust quantity and checkout from
            the cart. Checkout is Stripe test mode only.
          </p>
          {cartCount > 0 && (
            <button
              type="button"
              onClick={() => setIsCartOpen(true)}
              className="min-h-[44px] rounded-lg border border-black/20 bg-white px-4 py-2 text-sm font-semibold text-black hover:bg-gray-50"
            >
              View Cart ({cartCount})
            </button>
          )}
        </div>

        <div className="shop-licenses mx-auto max-w-4xl">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="single-license rounded-lg border border-black bg-white p-4 text-black shadow-lg md:p-8">
              <h2 className="mb-6 text-center text-2xl font-bold">
                Single License
              </h2>

              <div className="mb-8">
                <div className="mb-6 text-center text-3xl font-bold md:text-4xl">
                  $29.95
                </div>
                <p className="mb-4 text-center">
                  One-time purchase for lifetime access
                </p>
              </div>

              <div className="mb-8">
                <h3 className="mb-4 text-xl font-semibold">
                  Features Included:
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <span className="mr-2">✓</span>
                    Unlimited Collection Creation
                  </li>
                  <li className="flex items-center">
                    <span className="mr-2">✓</span>
                    Advanced Analytics & Reports
                  </li>
                  <li className="flex items-center">
                    <span className="mr-2">✓</span>
                    All Game Modes
                  </li>
                  <li className="flex items-center">
                    <span className="mr-2">✓</span>
                    Help via Feedback
                  </li>
                  <li className="flex items-center">
                    <span className="mr-2">✓</span>
                    Custom Themes
                  </li>
                  <li className="flex items-center">
                    <span className="mr-2">✓</span>
                    All Future Updates
                  </li>
                </ul>
              </div>

              <button
                type="button"
                onClick={() => handleAddToCart("single")}
                className="w-full rounded-lg bg-blue-500 px-6 py-3 text-lg font-bold text-white transition-all hover:bg-blue-600 active:bg-blue-700"
              >
                {isAuthenticated
                  ? "Add Single License to Cart"
                  : "Sign in to Add to Cart"}
              </button>
            </div>

            <div className="multi-license rounded-lg border border-black bg-white p-4 text-black shadow-lg md:p-8">
              <h2 className="mb-6 text-center text-2xl font-bold">
                Multi License Pack
              </h2>

              <div className="mb-8">
                <div className="mb-6 text-center text-3xl font-bold md:text-4xl">
                  $69.95
                </div>
                <p className="mb-4 text-center">
                  One-time purchase for 5 lifetime licenses
                </p>
              </div>

              <div className="mb-8">
                <h3 className="mb-4 text-xl font-semibold">
                  Everything in Single License, Plus:
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <span className="mr-2">✓</span>5 Separate License Keys
                  </li>
                  <li className="flex items-center">
                    <span className="mr-2">✓</span>
                    Bulk Account Management
                  </li>
                  <li className="flex items-center">
                    <span className="mr-2">✓</span>
                    50% Cost Savings
                  </li>
                  <li className="flex items-center">
                    <span className="mr-2">✓</span>
                    Team Analytics Dashboard
                  </li>
                  <li className="flex items-center opacity-0" aria-hidden="true">
                    <span className="mr-2">✓</span>
                    Spacer
                  </li>
                  <li className="flex items-center opacity-0" aria-hidden="true">
                    <span className="mr-2">✓</span>
                    Spacer
                  </li>
                </ul>
              </div>

              <button
                type="button"
                onClick={() => handleAddToCart("multi")}
                className="w-full rounded-lg bg-blue-500 px-6 py-3 text-lg font-bold text-white transition-all hover:bg-blue-600 active:bg-blue-700"
              >
                {isAuthenticated
                  ? "Add Multi License Pack to Cart"
                  : "Sign in to Add to Cart"}
              </button>
            </div>
          </div>

          <div className="mt-6 text-center text-sm text-gray-600">
            <p>
              Questions about Premium? Use <strong>Feedback</strong> in the menu
              above — we read every message and will get back to you.
            </p>
          </div>
        </div>

        <GuidedTour
          steps={tourStepsShop()}
          isRunning={isTourRunning}
          onComplete={handleTourComplete}
          currentStep={currentTourStep}
          onStepChange={handleTourStepChange}
          tourName="shop"
        />
      </div>
    </div>
  );
};

export default Shop;
